/**
 * Company page — About + Values + Careers.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Zap, Target, ShieldCheck, HeartHandshake, Rocket, Braces,
  MapPin, Briefcase, Clock, Users2, ArrowUpRight,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

const Kicker = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
    {children}
  </div>
);

const Section = ({ children, bg = "white", tid }) => {
  const bgClass = bg === "navy"
    ? "bg-[#0B1F3A] text-white"
    : bg === "slate"
    ? "bg-slate-50 text-slate-900"
    : "bg-white text-slate-900";
  return (
    <section data-testid={tid} className={bgClass}>
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">{children}</div>
    </section>
  );
};

const VALUES = [
  { icon: Zap, title: "Speed is the deal", desc: "Every millisecond of latency is a chance for the seller to change their mind. We optimise the whole surface for velocity." },
  { icon: Target, title: "Evidence over opinion", desc: "Every recommendation cites its source. Every metric has a version. No black boxes. Ever." },
  { icon: ShieldCheck, title: "Governance is table-stakes", desc: "Audit trails aren't optional. Every mutation is logged forever, hash-chained, and regulator-ready from day one." },
  { icon: HeartHandshake, title: "Investors, not spreadsheets", desc: "We build for the person doing the deal — not the analyst rebuilding a model at 11pm before a Monday call." },
  { icon: Rocket, title: "Ship every sprint", desc: "Build Packs are vertical slices that go live end-to-end. No half-finished features hidden behind flags." },
  { icon: Braces, title: "Deterministic under the AI", desc: "The AI is a synthesiser, not a decider. Every number under it is deterministic, testable, and reproducible." },
];

const ROLES = [
  { title: "Senior Frontend Engineer (React)", location: "Remote · US/EU", type: "Full-time", team: "Product" },
  { title: "Backend Engineer (FastAPI / Python)", location: "Remote · US/EU", type: "Full-time", team: "Engineering" },
  { title: "AI/ML Engineer (LLM Grounding)", location: "Remote · Global", type: "Full-time", team: "AI" },
  { title: "Customer Success Manager", location: "Remote · US", type: "Full-time", team: "CS" },
  { title: "Product Designer", location: "Remote · US/EU", type: "Full-time", team: "Design" },
  { title: "Data Engineer (Market Data)", location: "Remote · US", type: "Full-time", team: "Data" },
];

export default function CompanyPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="company-page">
      <MarketingHeader />

      {/* HERO */}
      <section data-testid="company-hero" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              About iSpeedToClose
            </div>
            <h1 data-testid="company-hero-headline" className="mt-6 text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              We build the operating system<br />
              <span className="text-emerald-400">for how real estate is done now.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              iSpeedToClose was born inside real acquisition teams — not a boardroom. We built the tooling we wished we had while chasing sellers on Sunday nights, arguing over which exit strategy pencils, and rebuilding the same spreadsheet for the 400th time.
            </p>
          </div>
        </div>
      </section>

      {/* STORY */}
      <Section tid="company-story" bg="white">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <Kicker>Our story</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">From acquisition floor to operating system.</h2>
          </div>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              We started as an acquisition team, not a software company. We were closing wholesale deals in Cleveland, running Sub-To and Novation structures in Texas, and building a rental portfolio in Ohio — all with the same broken toolkit every investor uses: a CRM bolted to a spreadsheet bolted to a text thread.
            </p>
            <p>
              The problem wasn&rsquo;t lack of tools. It was <strong>context loss</strong>. Every stage of the deal — lead, analysis, strategy, buyer match, contract, close — lived in a different tool. Every switch cost minutes. Every minute cost sellers. Every seller cost margin.
            </p>
            <p>
              So we built one command surface. Every property is a workspace. Every workspace has an immutable timeline. Every decision is scored by a deterministic engine and synthesised by an AI Coach that cites its evidence. And every action lives forever — for the auditor, the LP, and the acquisition manager who has to explain a decision six months later.
            </p>
            <p>
              iSpeedToClose is what we would have bought if it existed. So we shipped it.
            </p>
          </div>
        </div>
      </Section>

      {/* VALUES */}
      <Section tid="company-values" bg="slate">
        <Kicker>What we believe</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">Six principles behind every Build Pack.</h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {VALUES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-xl bg-white border border-slate-200 p-5 hover:border-emerald-500 hover:shadow-lg transition-all">
              <Icon className="w-6 h-6 text-emerald-600" />
              <div className="mt-3 font-bold">{title}</div>
              <div className="mt-2 text-sm text-slate-600">{desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* CAREERS */}
      <Section tid="company-careers" bg="white">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Kicker>Careers</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">Build the OS that real estate has been waiting for.</h2>
            <p className="mt-4 text-slate-600 max-w-2xl">
              We&rsquo;re a small, product-obsessed team building the deterministic engines and AI layer that professional real estate operators actually deserve. Remote-first, evidence-driven, and shipping every sprint.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="company-careers-cta"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
          >
            Send us a note <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-3">
          {ROLES.map((r) => (
            <Link
              key={r.title}
              to="/contact"
              data-testid={`company-role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-emerald-500 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-lg group-hover:text-emerald-700">{r.title}</div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {r.team}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.type}</span>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 rounded-xl bg-emerald-50 border border-emerald-200 p-5 flex items-start gap-3">
          <Users2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-emerald-900">Don&rsquo;t see your role?</div>
            <div className="mt-1 text-sm text-emerald-800">
              We&rsquo;re always talking to sharp operators, engineers, and designers. Send a note to{" "}
              <a href="mailto:careers@ispeedtoclose.com" className="underline font-semibold">
                careers@ispeedtoclose.com
              </a>{" "}
              with what you&rsquo;d want to build.
            </div>
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <section data-testid="company-final-cta" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Want to see it live?<br />
            <span className="text-emerald-400">We&rsquo;d love to show you.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="company-final-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
